import React, { ReactElement } from 'react';
import { Pagination } from '@mui/material';
import { AppCriteria, AppCriteriaField, AppResultInfo, AppSkinProps } from '../../../../types/decisions';
import { formatNumber, getFieldName } from '../../../../utils';
import { trackActivity } from '../../../../api/utils.api';

export interface AppResultsProps {
    results: AppSkinProps['results'];
    onRerank: AppSkinProps['onRerank'];
    onRerankMany: AppSkinProps['onRerankMany'];
    currentPage: number;
    shouldShowRankByManyCheckbox?: boolean;
    selectedProductIds: AppSkinProps['selectedProductIds'];
    setSelectedProductIds: AppSkinProps['setSelectedProductIds'];
    allowedFieldsIds: string[];
    criteriaData?: AppSkinProps['criteriaData'];
    criteriaConstraints?: AppSkinProps['criteriaConstraints'];
    chosenProductId?: string;
}

export const getRankLabel = (
    rank: number | string,
    scoreDisplay: string = 'percentage',
    scoreRanges: AppCriteria['config']['scoreRanges'] = [],
): ReactElement => {
    if (rank === '-') {
        return <>&nbsp;</>;
    }

    if (scoreDisplay === 'percentage') {
        return <>{rank}%</>;
    }

    const matchingRange = scoreRanges.find((range) => Number(rank) >= range.minScore);
    if (!matchingRange) {
        return <>{rank}%</>;
    }

    return <>{matchingRange.label.slice(0, 20) ?? `${rank}%`}</>;
};

export const renderSpecByType = (
    value: AppCriteriaField['val'],
    type: AppCriteriaField['type'],
): ReactElement => {
    if (value === null) {
        return <div>-</div>;
    }
    switch (type) {
        case 'select':
        case 'multiselect':
        case 'bool':
            return (
                <div>
                    {typeof value === 'object'
                        ? ((): ReactElement | ReactElement[] => {
                            const filtered = Object.entries(value).filter(
                                ([ _, v ]) => v !== '-1',
                            );

                            if (filtered.length === 0) {
                                return <div>-</div>;
                            }

                            const firstValue = filtered[0][1];
                            const allSame = filtered.every(([ _, v ]) => v === firstValue);
                            if (allSame) {
                                const keys = filtered.map(([ k ]) => k).join(', ');

                                return (
                                    <div className="multi-spec-item" key={keys}>
                                        {keys}
                                    </div>
                                );
                            }

                            return filtered.map(([ k, v ]) => (
                                <div className="multi-spec-item" key={k + v}>
                                    {k} ({v})
                                </div>
                            ));
                        })()
                        : value}
                </div>
            );
        case 'color':
            return (
                <div>
                    {typeof value === 'object' ? (
                        <div className="new-results-product-specs-spec-colors">
                            {Object.keys(value).map((key) => (
                                <div
                                    key={key + value[key]}
                                    className="new-results-product-specs-spec-color"
                                    style={{ backgroundColor: value[key] }}
                                />
                            ))}
                        </div>
                    ) : (
                        <div
                            className="new-results-product-specs-spec-color"
                            style={{ backgroundColor: value }}
                        />
                    )}
                </div>
            );
        case 'number':
            return <div>{formatNumber(value as unknown as number)}</div>;
        case 'text':
        case 'dynamic':
        case 'date':
        case 'zipcode':
            return <div>{value as string}</div>;
        case 'geoloc':
            const { latitude, longitude } = value as any;

            if (!latitude || !longitude) {
                return <div>-</div>;
            }

            return (
                <div> {latitude}, {longitude} </div>
            );

        default:
            return <div>-</div>;
    }
};

export const ResultProductCardSpecs = ({
    id,
    nonTechnicalFields,
    allowedFieldsIds,
}: {
    id: AppResultInfo['id'];
    nonTechnicalFields: AppResultInfo['nonTechnicalFields'];
    allowedFieldsIds: string[];
}) => {
    return (
        <div className="new-results-product-specs">
            {nonTechnicalFields.map(
                ({
                    id: itemId,
                    value,
                    name,
                    type,
                }) => {
                    if (!allowedFieldsIds.includes(itemId)) {
                        return null;
                    }
                    return (
                        <div className="new-results-product-specs-spec" key={id + itemId}>
                            <div>{getFieldName(name)}</div>

                            {renderSpecByType(value, type)}
                        </div>
                    );
                },
            )}
        </div>
    );
};

export const useResultActivityTracking = (criteriaData?: AppSkinProps['criteriaData']) => {
    const trackResultActivity = ({
        productId,
        event,
        action,
        field,
        link,
    }: {
        productId: string;
        event: string;
        action: string;
        field: string;
        link: string;
    }) => {
        if (!criteriaData?.id) {
            return;
        }
        trackActivity({
            app_id: criteriaData.id,
            event,
            action,
            field,
            row_id: productId,
            referenced_row_id: productId,
            url: link,
            link,
        }).catch(console.error);
    };

    const onProductLinkClick = (productId: string, productLink: string) => {
        trackResultActivity({
            productId,
            event: 'Product Link',
            action: 'productlink',
            field: 'productlink',
            link: productLink,
        });
        window.open(productLink, '_blank');
    };

    const onProductActionClick = (productId: string, actionLink: string) => {
        trackResultActivity({
            productId,
            event: 'Action Link',
            action: 'actionlink',
            field: 'actionlink',
            link: actionLink,
        });
        window.open(actionLink, '_blank');
    };

    return { trackResultActivity, onProductLinkClick, onProductActionClick };
};

export const ResultsPagination = ({
    pageInfo,
    dataset,
    onPageChange,
}: {
    pageInfo: AppSkinProps['pageInfo'];
    dataset: string;
    onPageChange: AppSkinProps['onPageChange'];
}) => (
    <div className="app-results-page-info">
        <Pagination
            page={pageInfo.current_page}
            count={pageInfo.total_pages}
            shape="rounded"
            onChange={(_, page) => onPageChange(dataset, page)}
        />
    </div>
);
